import { t } from './lib/i18n.js';
// ── Shared UI components ──

import { escapeHtml } from './utils.js';

// ── Toast notifications ──

function ensureToastContainer() {
  let c = document.getElementById('toast-container');
  if (!c) {
    c = document.createElement('div');
    c.id = 'toast-container';
    c.className = 'toast-container';
    document.body.appendChild(c);
  }
  return c;
}

export function showToast(message, type = 'info') {
  const container = ensureToastContainer();
  const el = document.createElement('div');
  el.className = `toast toast-${type}`;
  el.textContent = message;
  container.appendChild(el);
  setTimeout(() => {
    el.style.opacity = '0';
    el.style.transition = 'opacity 0.3s';
    setTimeout(() => el.remove(), 300);
  }, 3000);
}

// ── Modal ──

let _modalOnClose = null;
// Owner token of the open modal (TPT647). openModal() returns a fresh one (or keeps
// `options.owner`); closeModal(token) acts only while that token still owns the modal, so a
// page teardown that runs late cannot close a modal the page now on screen has opened.
class ModalOwner {}
let _modalOwner = null;
let _modalOpener = null;       // the element that had keyboard focus when the open modal opened (TPT9176)
let _modalEscHandler = null;   // the open modal's document Escape listener, detached on close
let _modalRouteHandler = null; // its window hashchange listener (options.closeOnNavigate)

// The element that has keyboard focus, or null while focus is on the body.
function focusedElement() {
  const active = document.activeElement;
  return active && active !== document.body ? active : null;
}

// Route path of the current hash, without its `?query` — a query-only change is not navigation.
function routePath() {
  const raw = (window.location.hash || '').slice(1);
  const q = raw.indexOf('?');
  return q >= 0 ? raw.slice(0, q) : raw;
}

// Returns the modal's owner token. options.owner (a token from an earlier openModal()) keeps
// that token across a replacement, so a multi-step flow stays closable by its first token.
// options.closeOnNavigate closes the modal when the route path changes — for modals no page
// owns (navbar, sidebar), which no page teardown will close.
export function openModal(title, bodyHTML, options = {}) {
  // closeModal() gives keyboard focus back to the element that has it now (TPT9176). A step that
  // replaces its own flow's modal keeps the first step's opener: by now focus is in that modal.
  const opener = options.owner instanceof ModalOwner && options.owner === _modalOwner
    ? _modalOpener
    : focusedElement();
  removeModal(); // a replaced modal does not give focus back: the new one takes over
  const token = options.owner instanceof ModalOwner ? options.owner : new ModalOwner();
  _modalOwner = token;
  _modalOpener = opener;
  _modalOnClose = typeof options.onClose === 'function' ? options.onClose : null;
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <h3>${escapeHtml(title)}</h3>
        <button class="btn btn-ghost btn-sm" id="modal-close">&times;</button>
      </div>
      <div class="modal-body">${bodyHTML}</div>
    </div>`;

  document.body.appendChild(overlay);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeModal(token);
  });
  overlay.querySelector('#modal-close').addEventListener('click', () => closeModal(token));

  _modalEscHandler = (e) => {
    if (e.key === 'Escape') closeModal(token);
  };
  document.addEventListener('keydown', _modalEscHandler);

  if (options.closeOnNavigate) {
    const openedAt = routePath();
    _modalRouteHandler = () => {
      if (routePath() !== openedAt) closeModal(token);
    };
    window.addEventListener('hashchange', _modalRouteHandler);
  }

  const firstInput = overlay.querySelector('input, textarea');
  if (firstInput) firstInput.focus();

  if (options.onMount) options.onMount(overlay);
  return token;
}

// With an owner token (or null) it acts only while that token owns the open modal; null never
// owns one. Anything else — no argument, or the click Event a `addEventListener('click',
// closeModal)` caller passes — closes whatever modal is open, as before TPT647.
// Focus inside the modal falls to the body when the modal is removed, so it goes back to the
// element that opened the modal (TPT9176) — only while that element is still on the page and
// focus is still on the body: an element the user or onClose focused keeps focus.
export function closeModal(token) {
  if ((token === null || token instanceof ModalOwner) && token !== _modalOwner) return;
  const opener = removeModal();
  if (opener?.isConnected && document.activeElement === document.body) opener.focus();
}

// Detaches the open modal's listeners, removes #modal-overlay and runs its onClose. Returns the
// element that had keyboard focus when the modal opened, or null when no modal was open.
function removeModal() {
  const opener = _modalOpener;
  _modalOwner = null;
  _modalOpener = null;
  if (_modalEscHandler) {
    document.removeEventListener('keydown', _modalEscHandler);
    _modalEscHandler = null;
  }
  if (_modalRouteHandler) {
    window.removeEventListener('hashchange', _modalRouteHandler);
    _modalRouteHandler = null;
  }
  const overlay = document.getElementById('modal-overlay');
  if (!overlay) return null;
  overlay.remove();
  const cb = _modalOnClose;
  _modalOnClose = null;
  if (cb) {
    try { cb(); } catch (_) {}
  }
  return opener;
}

// True while `token` (an openModal() owner token) still owns the open modal. A multi-step
// flow checks it after each await: false means the user closed the modal or another one
// replaced it, and the flow must not open its next step.
export function isModalOpen(token) {
  return token != null && token === _modalOwner;
}

// ── Theme (tt-web-theme, C1265) ──
//
// Two layers land on the same <html data-theme="..."> attribute:
//   - workspace theme  — localStorage['tt-theme'], user's light/dark + palette pick
//   - project override — project.color_scheme, applied while a project is in context
// applyThemeForContext() is the single entry point both layers funnel through; it is
// called by layout.js's updateLayoutContext() on every route (the app's one existing
// "current project" broadcast — no router change needed). See tt-web-theme.md.

const SUN_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>';
const MOON_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z"/></svg>';

// C1267 — 21 named palettes (11 light, 10 dark), each with a matching [data-theme] block
// in styles.css. id/label/mode is the whole contract a new entry needs to fulfil; this
// is the single client-side source of truth for "what can this app actually paint" —
// settings.js's Appearance-tab radio-card grid (COLOR_SCHEME_GROUPS /
// renderColorSchemeOptions, C1303 — the tab's only control now) derives from getThemes()
// rather than keeping its own copy. ORDER MATTERS: the first light and first dark entry
// are the workspace defaults (MODE_DEFAULT below) — paper/ink, the brand palettes (TPT214).
const THEMES = [
  { id: 'paper', label: 'Light › Paper', mode: 'light' },
  { id: 'parchment', label: 'Light › Parchment', mode: 'light' },
  { id: 'latte', label: 'Light › Sage Morning', mode: 'light' },
  { id: 'meadow', label: 'Light › Meadow Mist', mode: 'light' },
  { id: 'arctic', label: 'Light › Arctic Haze', mode: 'light' },
  { id: 'terracotta', label: 'Light › Terracotta', mode: 'light' },
  { id: 'sakura', label: 'Light › Sakura Bloom', mode: 'light' },
  { id: 'citrus', label: 'Light › Citrus Grove', mode: 'light' },
  { id: 'slate', label: 'Light › Slate Grey', mode: 'light' },
  { id: 'lagoon', label: 'Light › Lagoon', mode: 'light' },
  { id: 'iris', label: 'Light › Lavender Fields', mode: 'light' },
  { id: 'ink', label: 'Dark › Ink', mode: 'dark' },
  { id: 'mocha', label: 'Dark › Midnight Mocha', mode: 'dark' },
  { id: 'ocean', label: 'Dark › Ocean Depths', mode: 'dark' },
  { id: 'ember', label: 'Dark › Ember Dusk', mode: 'dark' },
  { id: 'forest', label: 'Dark › Forest Night', mode: 'dark' },
  { id: 'nord', label: 'Dark › Nordic Frost', mode: 'dark' },
  { id: 'rose', label: 'Dark › Rose Quartz', mode: 'dark' },
  { id: 'graphite', label: 'Dark › Graphite', mode: 'dark' },
  { id: 'espresso', label: 'Dark › Espresso', mode: 'dark' },
  { id: 'neon', label: 'Dark › Neon Circuit', mode: 'dark' },
];
// C1303 — workspace layer (non-project routes) is pinned to exactly the FIRST light and
// FIRST dark entry in THEMES, not a hand-picked id: with the Appearance tab now editing
// only the project's color_scheme (settings.js), there is no picker left for the
// workspace preference, so "which theme" must be a structural fact about THEMES' order
// (paper/ink today, TPT214), not a separate constant that can drift from it.
const MODE_DEFAULT = {
  light: THEMES.find(t => t.mode === 'light')?.id || 'paper',
  dark: THEMES.find(t => t.mode === 'dark')?.id || 'ink',
};

// Current route context, set by applyThemeForContext() — re-consulted whenever the
// light/dark switch flips so a flip never fights an active project override.
let _ctxProjectId = null;
let _ctxColorScheme = null;

export function getThemes() {
  return THEMES;
}

// Non-'default' id the web CSS actually implements. 'default' means "no project
// override" (api/src/lib/color-schemes.js) — never a real palette. Every other value in
// api/src/lib/color-schemes.js's COLOR_SCHEMES has a matching [data-theme] block here
// (C1267) — this membership check exists as a defensive fallback for a value the API
// accepts that this app's CSS hasn't caught up to yet, never an unstyled attribute.
export function isKnownScheme(s) {
  return typeof s === 'string' && s !== 'default' && THEMES.some(t => t.id === s);
}

export function getTheme() {
  const stored = localStorage.getItem('tt-theme');
  if (stored === MODE_DEFAULT.light || stored === MODE_DEFAULT.dark) return stored;
  // C1303 — the workspace layer no longer has a palette picker (Appearance now edits the
  // project's color_scheme instead), so a leftover stored id from before this change
  // collapses to the first theme of its own mode rather than staying picked forever.
  return MODE_DEFAULT[themeMode(stored)];
}

function themeMode(id) {
  return THEMES.find(t => t.id === id)?.mode || 'dark';
}

export function getThemeMode() {
  return themeMode(getTheme());
}

// Resolves what should actually render right now: project override wins while one is
// in context, else the workspace theme.
function resolveTheme() {
  return isKnownScheme(_ctxColorScheme) ? _ctxColorScheme : getTheme();
}

function applyDom(themeId, projectId) {
  document.documentElement.dataset.theme = themeId;
  // Cache only a real project override. Caching the workspace fallback for a project with
  // color_scheme='default' would flash stale colors before the next workspace resolution.
  if (projectId !== null && projectId !== undefined && projectId !== '' && isKnownScheme(_ctxColorScheme)) {
    try { localStorage.setItem('tt-project-theme:' + projectId, themeId); } catch { /* anti-flash cache only */ }
  }
  updateThemeToggleUi();
}

// setTheme() sets the WORKSPACE preference. Routes through resolveTheme() so it can
// never stomp an active project override. Only caller left is setThemeMode() below —
// C1303 removed the Appearance tab's direct call (it now edits the project's
// color_scheme via updateLayoutContext instead).
export function setTheme(t) {
  localStorage.setItem('tt-theme', t);
  applyDom(resolveTheme(), _ctxProjectId);
}

// setThemeMode() is what the navbar light/dark switch drives. C1303 — with no palette
// picker left for the workspace layer, "last theme used in this mode" no longer applies;
// this always lands on that mode's one fixed theme (MODE_DEFAULT).
export function setThemeMode(mode) {
  setTheme(MODE_DEFAULT[mode]);
}

// Single entry point every route funnels through (called from layout.js's
// updateLayoutContext — the app's one "current project changed" broadcast).
// projectId truthy => switch hides; colorScheme unknown/absent => falls through to the
// workspace theme (graceful degradation for any API-valid scheme this app's CSS hasn't
// caught up to yet).
export function applyThemeForContext({ projectId, colorScheme } = {}) {
  _ctxProjectId = projectId === undefined || projectId === null || projectId === '' ? null : projectId;
  // A project palette is scoped to project routes. Projectless routes must always resolve
  // from the workspace preference, even if a stale caller passes colorScheme through.
  _ctxColorScheme = _ctxProjectId !== null ? colorScheme || null : null;
  applyDom(resolveTheme(), _ctxProjectId);
}

// Anti-flash boot: read #/projects/:id straight off the hash (before router.js has even
// loaded) and apply its cached palette, if any, so first paint doesn't flash the
// workspace theme then repaint. Mirrors the Task App's localStorage['tiptask-theme']
// cache (tt-project-config.md).
export function bootTheme() {
  const m = /^#\/projects\/([^/]+)/.exec(window.location.hash);
  const projectId = m ? m[1] : null;
  let cached = null;
  if (projectId) {
    try { cached = localStorage.getItem('tt-project-theme:' + projectId); } catch { cached = null; }
  }
  _ctxProjectId = projectId;
  _ctxColorScheme = null; // real color_scheme arrives once layout.js resolves the project
  const workspace = getTheme();
  // TPT214 — a stored id may predate paper/ink (e.g. 'parchment'/'mocha'). getTheme()
  // collapses it forward on read so this boot already paints the new default; this retires
  // it in storage once so tt-theme stops carrying a default that no longer exists. A browser
  // with nothing stored stays that way — no preference is invented for it.
  try {
    const stored = localStorage.getItem('tt-theme');
    if (stored !== null && stored !== workspace) localStorage.setItem('tt-theme', workspace);
  } catch { /* normalization is best-effort */ }
  applyDom(isKnownScheme(cached) ? cached : workspace, null);
}

function updateThemeToggleUi() {
  const btn = document.getElementById('theme-toggle');
  if (!btn) return;
  btn.hidden = _ctxProjectId !== null;
  const dark = getThemeMode() === 'dark';
  btn.setAttribute('aria-checked', String(dark));
  btn.classList.toggle('is-dark', dark);
}

// Renders already synced to current state (bootTheme() has run by the time navbar
// mounts, so _ctxProjectId/mode are known) — avoids a flash of a wrongly-visible or
// wrong-mode switch before the first updateLayoutContext() call lands.
export function renderThemeToggle() {
  const dark = getThemeMode() === 'dark';
  return `<button class="theme-switch${dark ? ' is-dark' : ''}" id="theme-toggle" type="button" role="switch" aria-checked="${dark}" data-i18n-title="navbar.theme" title="${t('navbar.theme')}" data-i18n-aria-label="navbar.toggleTheme" aria-label="${t('navbar.toggleTheme')}"${_ctxProjectId ? ' hidden' : ''}>
    <span class="theme-switch-icon theme-switch-sun">${SUN_SVG}</span>
    <span class="theme-switch-track"><span class="theme-switch-knob"></span></span>
    <span class="theme-switch-icon theme-switch-moon">${MOON_SVG}</span>
  </button>`;
}

export function bindThemeToggle() {
  document.getElementById('theme-toggle')?.addEventListener('click', () => {
    setThemeMode(getThemeMode() === 'dark' ? 'light' : 'dark');
  });
}

// ── Project color schemes (C1266, expanded C1267) ──
// Mirrors api/src/lib/color-schemes.js COLOR_SCHEMES. Literal hexes, not var(--c-*) — a var
// resolves against the viewer's CURRENT theme, so every project's badge would shift color
// together on theme toggle, defeating the point of a per-project scheme. Same reasoning as
// the Task App's WORKFLOW_COLOR_SWATCHES (ai/todo/server/src/client/status-registry.js).
// Each value is that scheme's --c-primary hex (see the matching [data-theme] block in
// styles.css) — swatch and palette always agree.
// Public palette metadata for project-owned surfaces. Keep these values literal so a
// workspace theme change cannot repaint an already-rendered project's badge or chip.
export const COLOR_SCHEME_SWATCHES = Object.freeze({
  // 'default' is not a palette — it means "no project override", so its swatch tracks the
  // workspace default's primary (ink's teal, TPT214) instead of a fixed unrelated hue.
  default: '#14b8a6',
  paper: '#0e8377',
  parchment: '#7c3aed',
  latte: '#8839ef',
  meadow: '#059669',
  arctic: '#2563eb',
  terracotta: '#dc2626',
  sakura: '#db2777',
  citrus: '#ca8a04',
  slate: '#475569',
  lagoon: '#0d9488',
  iris: '#7c5cff',
  ink: '#14b8a6',
  mocha: '#cba6f7',
  ocean: '#4fc3f7',
  ember: '#ff9f5a',
  forest: '#72d391',
  nord: '#88c0d0',
  rose: '#ebbcba',
  graphite: '#b8c0cc',
  espresso: '#d7a86e',
  neon: '#00e5ff',
});

// Hash fallback for a project that never picked a scheme ('default'), so unset projects stay
// visually distinct from each other. Was PROJECT_COLORS, duplicated in dashboard.js and
// workspace-tasks.js — now the single shared copy.
const PROJECT_COLORS = [
  'var(--c-rosewater)', 'var(--c-flamingo)', 'var(--c-pink)', 'var(--c-mauve)',
  'var(--c-red)', 'var(--c-maroon)', 'var(--c-peach)', 'var(--c-yellow)',
  'var(--c-green)', 'var(--c-teal)', 'var(--c-sky)', 'var(--c-blue)', 'var(--c-lavender)',
];

// Valid CSS color for any known color_scheme value. With `fallback` given, 'default' (and any
// unrecognized value) returns `fallback` instead of the neutral default swatch.
export function colorSchemeSwatch(scheme, fallback) {
  if (fallback !== undefined && (!scheme || scheme === 'default' || !COLOR_SCHEME_SWATCHES[scheme])) {
    return fallback;
  }
  return COLOR_SCHEME_SWATCHES[scheme] || COLOR_SCHEME_SWATCHES.default;
}

// Badge/chip color for a project: its own scheme's swatch, or a per-id hash color while
// color_scheme is still 'default'.
export function projectSwatchColor(scheme, projectId) {
  return colorSchemeSwatch(scheme, PROJECT_COLORS[(projectId || 0) % PROJECT_COLORS.length]);
}

// ── Per-scheme accent ramps (C1304) ──
// Status chips store a bare token name ('green'/'blue'/'overlay1' —
// api/src/lib/task-statuses.js), not a color. COLOR_SCHEME_SWATCHES above gives one hex
// per scheme (its --c-primary); a status chip needs the whole 13-accent + overlay0/1 ramp
// so a token can be resolved to a literal hex inside a SPECIFIC project's palette instead
// of the viewer's own theme. Hand-mirrors the [data-theme] blocks in styles.css — same
// drift risk as COLOR_SCHEME_SWATCHES, now 15 values per scheme instead of 1; keep both
// in sync with styles.css by hand (see tt-web-theme.md § Cross-app parity).
//
// This is not a shortcut — it mirrors styles.css's real structure: all 11 light schemes
// reuse the Latte 13 accents byte-for-byte (only overlay0/1 differ), graphite (neutral)
// reuses Mocha's 13 verbatim, and the remaining 8 dark schemes each get a bespoke set.
const LATTE_ACCENTS = Object.freeze({
  rosewater: '#dc8a78', flamingo: '#dd7878', pink: '#ea76cb', mauve: '#8839ef',
  red: '#d20f39', maroon: '#e64553', peach: '#fe640b', yellow: '#df8e1d',
  green: '#40a02b', teal: '#179299', sky: '#04a5e5', blue: '#1e66f5', lavender: '#7287fd',
});
const MOCHA_ACCENTS = Object.freeze({
  rosewater: '#f5e0dc', flamingo: '#f2cdcd', pink: '#f5c2e7', mauve: '#cba6f7',
  red: '#f38ba8', maroon: '#eba0ac', peach: '#fab387', yellow: '#f9e2af',
  green: '#a6e3a1', teal: '#94e2d5', sky: '#89dceb', blue: '#89b4fa', lavender: '#b4befe',
});
const OCEAN_ACCENTS = Object.freeze({
  rosewater: '#f2d5cf', flamingo: '#eebebe', pink: '#f4b8e4', mauve: '#b8c0ff',
  red: '#ff7f8f', maroon: '#f29ca3', peach: '#ffb27d', yellow: '#f7d774',
  green: '#8fe3b0', teal: '#67d8d0', sky: '#4fc3f7', blue: '#65aef7', lavender: '#9cb7ff',
});
const EMBER_ACCENTS = Object.freeze({
  rosewater: '#ffd0c2', flamingo: '#ffb3a7', pink: '#ff9fcb', mauve: '#d2a6ff',
  red: '#ff7a7a', maroon: '#e86f67', peach: '#ffae62', yellow: '#f7cd73',
  green: '#9ed67d', teal: '#76d2b4', sky: '#8bd3ff', blue: '#9dbdff', lavender: '#c5b6ff',
});
const FOREST_ACCENTS = Object.freeze({
  rosewater: '#f0d0c4', flamingo: '#e9b8ab', pink: '#ef9ed2', mauve: '#b9a8ff',
  red: '#ff817c', maroon: '#ef928a', peach: '#f3b06b', yellow: '#d8d876',
  green: '#72d391', teal: '#64d6bd', sky: '#79cceb', blue: '#8ab5ff', lavender: '#a9b8ff',
});
const NORD_ACCENTS = Object.freeze({
  rosewater: '#e8d9d3', flamingo: '#dcb8ae', pink: '#d9a3c0', mauve: '#b48ead',
  red: '#bf616a', maroon: '#a54242', peach: '#d08770', yellow: '#ebcb8b',
  green: '#a3be8c', teal: '#8fbcbb', sky: '#88c0d0', blue: '#81a1c1', lavender: '#7f96c9',
});
const ROSE_ACCENTS = Object.freeze({
  rosewater: '#f2d5cf', flamingo: '#ea9a97', pink: '#d999b9', mauve: '#c4a7e7',
  red: '#eb6f92', maroon: '#d4607e', peach: '#f6c177', yellow: '#e0c264',
  green: '#7fa88a', teal: '#9ccfd8', sky: '#b3d6dc', blue: '#7d9cc4', lavender: '#b9a3d9',
});
const ESPRESSO_ACCENTS = Object.freeze({
  rosewater: '#f0d0bc', flamingo: '#eabf9e', pink: '#e6a893', mauve: '#d3a37e',
  red: '#e2836f', maroon: '#c96a5a', peach: '#e8a253', yellow: '#e0c25c',
  green: '#a8c47a', teal: '#7fb8a8', sky: '#8fbdd6', blue: '#7f9fc9', lavender: '#a99bd6',
});
const NEON_ACCENTS = Object.freeze({
  rosewater: '#ffd9ec', flamingo: '#ff9ecf', pink: '#ff6ec7', mauve: '#b06bff',
  red: '#ff5470', maroon: '#d43f63', peach: '#ff9e5e', yellow: '#f5e04a',
  green: '#39ff9e', teal: '#33f0d0', sky: '#00e5ff', blue: '#4ea9ff', lavender: '#9d7bff',
});
// TPT214 — the brand dark palette's warm-neutral set (styles.css [data-theme="ink"]);
// peach is the marketing guide's second accent (--tt-sanguine).
const INK_ACCENTS = Object.freeze({
  rosewater: '#f3e6d9', flamingo: '#eccbb6', pink: '#e9b2a3', mauve: '#c9b0c6',
  red: '#e8897a', maroon: '#d4826c', peach: '#e08763', yellow: '#e3c877',
  green: '#a9cf8f', teal: '#7fd6c6', sky: '#8fc9d1', blue: '#8aafcb', lavender: '#aeb0d2',
});

const ramp = (accents, overlay0, overlay1) => Object.freeze({ ...accents, overlay0, overlay1 });

// scheme id → { <13 accent names>, overlay0, overlay1 } — 15 literal hexes, the full set
// a status color token can name (settings.js's Workflow-tab color picker offers exactly
// these 15). No 'default' entry — 'default' means "no project override", resolved by
// schemeAccentColor() falling through to var(--c-<token>) instead.
export const COLOR_SCHEME_ACCENTS = Object.freeze({
  paper: ramp(LATTE_ACCENTS, '#b9b4a6', '#857f72'),
  parchment: ramp(LATTE_ACCENTS, '#9599ac', '#7f8399'),
  latte: ramp(LATTE_ACCENTS, '#9ca0b0', '#8c8fa1'),
  meadow: ramp(LATTE_ACCENTS, '#94b09c', '#7d9c86'),
  arctic: ramp(LATTE_ACCENTS, '#93a9cc', '#7d94bd'),
  terracotta: ramp(LATTE_ACCENTS, '#c2949a', '#b57d86'),
  sakura: ramp(LATTE_ACCENTS, '#c98aa8', '#b06e8c'),
  citrus: ramp(LATTE_ACCENTS, '#ac9860', '#8f7c48'),
  slate: ramp(LATTE_ACCENTS, '#99a2ae', '#808a99'),
  lagoon: ramp(LATTE_ACCENTS, '#6fa89f', '#4f8c82'),
  iris: ramp(LATTE_ACCENTS, '#9a8cd2', '#8072bf'),
  ink: ramp(INK_ACCENTS, '#56564e', '#8c8c82'),
  mocha: ramp(MOCHA_ACCENTS, '#6c7086', '#7f849c'),
  graphite: ramp(MOCHA_ACCENTS, '#57575f', '#6e6e77'),
  ocean: ramp(OCEAN_ACCENTS, '#5e7892', '#86a0b7'),
  ember: ramp(EMBER_ACCENTS, '#8a6758', '#b08a78'),
  forest: ramp(FOREST_ACCENTS, '#5f806d', '#85a491'),
  nord: ramp(NORD_ACCENTS, '#616e88', '#7688a8'),
  rose: ramp(ROSE_ACCENTS, '#524f67', '#6e6a86'),
  espresso: ramp(ESPRESSO_ACCENTS, '#6b5a4a', '#85715e'),
  neon: ramp(NEON_ACCENTS, '#3a4a70', '#55698f'),
});

// Literal accent hex for one status color token (e.g. 'green', 'overlay1') inside a
// specific project's palette. 'default' (no project override), a scheme this app's CSS
// hasn't caught up to yet, or a missing token all fall through to var(--c-<token>) — the
// viewer's OWN theme, which is exactly what such a project renders in when opened (same
// graceful-degradation contract as isKnownScheme()). Never interpolates an unvalidated
// token into the var() name — an unknown token still falls through to the var() path
// rather than emitting a bogus custom-property reference.
export function schemeAccentColor(scheme, token) {
  const safeToken = token || 'overlay1';
  const accents = isKnownScheme(scheme) ? COLOR_SCHEME_ACCENTS[scheme] : null;
  if (accents) return accents[safeToken] || accents.overlay1;
  return `var(--c-${safeToken})`;
}

// ── Per-scheme surface pair (C1472) ──
// COLOR_SCHEME_SWATCHES gives a scheme's accent, COLOR_SCHEME_ACCENTS its 15-token ramp.
// Neither says what that scheme's *ground* is, so a project card painted with only an
// accent still sits on the VIEWER's background — dashboard project cards and
// .ws-project-badge chips looked identical regardless of a light vs. dark color_scheme
// (C1472). `bg` mirrors that scheme's --c-surface0 (what .card already paints via
// --c-bg-card) and `text` its --c-text, so a project-owned surface carries its own
// light/dark ground AND stays legible on it. Literal hexes for the same reason as
// COLOR_SCHEME_SWATCHES — a var() would resolve against the viewer's CURRENT theme. No
// 'default' entry: 'default' means "no project override", and every accessor below
// returns null/'' so callers fall through to the theme-driven value unchanged. Hand-
// mirrors the [data-theme] blocks in styles.css, same drift risk as the other two tables —
// see tt-web-theme.md § Cross-app parity.
const surface = (bg, text) => Object.freeze({ bg, text });

export const COLOR_SCHEME_BACKGROUNDS = Object.freeze({
  paper:      surface('#e9e4d8', '#1a1a19'),
  parchment:  surface('#dadce4', '#262a3d'),
  latte:      surface('#d5d9e1', '#4c4f69'),
  meadow:     surface('#dce8e2', '#1a2e1a'),
  arctic:     surface('#dce4ef', '#1a1e2e'),
  terracotta: surface('#eddcdc', '#2e1a1a'),
  sakura:     surface('#f5dae6', '#2e1a24'),
  citrus:     surface('#ece2cc', '#2e2611'),
  slate:      surface('#dfe3e8', '#1c222b'),
  lagoon:     surface('#d5e8e5', '#122b28'),
  iris:       surface('#e2ddf6', '#211a3a'),
  ink:        surface('#2a2a28', '#f4f1ea'),
  mocha:      surface('#313244', '#cdd6f4'),
  ocean:      surface('#18324a', '#d6e7f3'),
  ember:      surface('#3b241c', '#f6ded2'),
  forest:     surface('#1b3528', '#d7eadf'),
  nord:       surface('#3b4252', '#eceff4'),
  rose:       surface('#26233a', '#e0def4'),
  graphite:   surface('#2a2a2e', '#e8e8ea'),
  espresso:   surface('#322822', '#f0e4d6'),
  neon:       surface('#141b2b', '#dff4fb'),
});

// Literal background hex for a scheme's project-owned surfaces (cards, chips). null for
// 'default' (no override) or an unrecognized id — callers keep painting the theme-driven
// background in that case, same graceful-degradation contract as isKnownScheme().
export function schemeBackgroundColor(scheme) {
  return isKnownScheme(scheme) ? COLOR_SCHEME_BACKGROUNDS[scheme].bg : null;
}

// Literal text hex to pair with schemeBackgroundColor() — a project surface painted with
// only a background and the viewer's own --c-text would go dark-text-on-dark (or the
// reverse) whenever the scheme's mode differs from the viewer's workspace theme.
export function schemeTextColor(scheme) {
  return isKnownScheme(scheme) ? COLOR_SCHEME_BACKGROUNDS[scheme].text : null;
}

// (TPT9132) OKLCH lightness bounds, [min, max], for text painted in a project's own accent
// (--proj-accent-text in styles.css: recent task keys, the in-progress count, project
// badges). A swatch is a fill colour, mostly a mid-tone under 4.5:1 on its own light
// ground, so the text repaints it with its lightness clamped into its mode's range, hue and
// chroma kept: light modes cap it at 0.38 and dark modes floor it at 0.86, a shade past
// --c-primary-text's 0.44/0.84 since a badge puts it on a 15% tint of itself.
// Keyed on the PROJECT scheme's mode, not the viewer's: a project surface carries its own
// ground. Mirrors the --c-accent-text-l-min/max groups in styles.css; ui.test.js checks both.
export const ACCENT_TEXT_LIGHTNESS = Object.freeze({
  light: Object.freeze([0, 0.38]),
  dark: Object.freeze([0.86, 1]),
});

// Inline-style fragment (leading ';', or '' for no override) carrying the custom properties
// a project-owned surface needs: --proj-bg/--proj-text direct from the table, plus
// --proj-muted/--proj-track derived here via color-mix() so callers don't need two
// more hand-mirrored table columns, and the scheme mode's --proj-accent-l-min/max
// (TPT9132). Append to an existing style="--proj-color:…" — every CSS site keeps its own
// pre-C1472 fallback, so '' leaves rendering unchanged for 'default' projects (their
// accent text takes the viewer's bounds instead).
export function schemeSurfaceStyle(scheme) {
  const bg = schemeBackgroundColor(scheme);
  if (!bg) return '';
  const text = schemeTextColor(scheme);
  const [min, max] = ACCENT_TEXT_LIGHTNESS[themeMode(scheme)];
  return `;--proj-bg:${bg};--proj-text:${text};` +
    `--proj-muted:color-mix(in srgb, ${text} 65%, ${bg});` +
    `--proj-track:color-mix(in srgb, ${text} 12%, ${bg});` +
    `--proj-accent-l-min:${min};--proj-accent-l-max:${max};`;
}

// Backward-compat alias
export const esc = escapeHtml;
